import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'

import App from './App'
import ErrorBoundary from './components/ErrorBoundary'
import { registerServiceWorker, syncGrantedPushSubscription } from './lib/sw'
import { startOutboxAutoDrain } from './lib/outbox'
import {
  hydrateLocalReadSnapshotScope,
  hydrateLocalReadSnapshots,
  installLocalReadStorePersistence,
} from './lib/localReadStore'
import { applyPendingNotificationNavBeforeReactMount } from './lib/notificationNav'
import { preloadThreadViewComponents } from './lib/preloadChatViews'
import { hydrateNativeNotificationCacheBeforeReactMount } from './lib/nativePush'
import { startAgentBannerWatchdog } from './lib/agentBannerWatchdog'
import './index.css'

const guestBoot = window.location.pathname === '/shared'

// Register the service worker for offline app shell + push handler.
if (!guestBoot) {
void registerServiceWorker().then((reg) => {
  void syncGrantedPushSubscription(reg, { createIfMissing: true }).catch((err) => {
    console.warn('Push subscription self-heal failed:', err)
  })
})
// Background drain of any queued outgoing messages on reconnect.
startOutboxAutoDrain()
// Self-heal the native test-bridge "reading app state" banner so it can never
// stay stuck on screen after an agent run ends (see agentBannerWatchdog).
startAgentBannerWatchdog()
// If Android delivered the notification tap before React mounted, route to
// that target before ChatListPage can render and fan out list-wide fetches.
applyPendingNotificationNavBeforeReactMount()
}
function notificationBootTarget(): { chatId: string; topicId: string | null } | null {
  try {
    const url = new URL(window.location.href)
    if (url.searchParams.get('notification_open') !== '1') return null
    const pathMatch = url.pathname.match(/^\/chats\/([^/]+)$/)
    const chatId = url.searchParams.get('chat_id') || (pathMatch?.[1] ? decodeURIComponent(pathMatch[1]) : '')
    if (!chatId) return null
    const topicId = url.searchParams.get('topic_id') || url.searchParams.get('topic') || null
    return { chatId, topicId: topicId || null }
  } catch {
    return null
  }
}

// Hydrate the synchronous in-memory snapshot from IDB before React mounts so
// the first render of ChatListPage / ChatPage on a notification tap has
// placeholderData available. Cold IDB read is ~10–50ms on phones for the
// whole vault; this is much faster than the per-component async read it
// replaces. The hydrate helper catches storage failures, so awaiting it does
// not block boot on unavailable IndexedDB.
const notificationTarget = notificationBootTarget()
// Widget destinations need no stored history; do not wait for the whole IDB vault.
const widgetBoot = window.location.pathname === '/chats/first-free'
  || window.location.pathname.startsWith('/code/new/')
const localReadHydration = guestBoot ? Promise.resolve() : widgetBoot
  ? Promise.resolve().finally(() => { void hydrateLocalReadSnapshots() })
  : notificationTarget
  ? hydrateLocalReadSnapshotScope(notificationTarget.chatId, notificationTarget.topicId)
      .then(() => hydrateNativeNotificationCacheBeforeReactMount())
      .finally(() => { void hydrateLocalReadSnapshots() })
  : hydrateLocalReadSnapshots().finally(() => {
      void hydrateNativeNotificationCacheBeforeReactMount()
    })
// Eager-load the core thread-view chunks during the same boot tick so a
// cold-tap on a notification doesn't have to wait for the MessageList chunk
// download before painting. Source-card chunks stay topic-gated so boot does
// not fetch Email/Todoist/Commitment/Event/Tweet code for unrelated opens.
preloadThreadViewComponents()

const qc = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // WhatsApp-grade: aggressive transient-failure recovery. 5 retries
      // with capped exponential backoff (200ms, 400ms, 800ms, 1.6s, 3s)
      // means a controller restart, Caddy reload, or 3-second network
      // blip never reaches the user as a visible error. Auth failures
      // (401/403) and explicit 4xx don't retry — those need user action.
      retry: (failureCount: number, err: unknown) => {
        const msg = String((err as Error)?.message ?? '')
        if (/\b(401|403|404|400|allowlist|unauthor|forbid)/i.test(msg)) {
          return false
        }
        return failureCount < 5
      },
      retryDelay: (attempt: number) => Math.min(3000, 200 * 2 ** attempt),
      refetchOnWindowFocus: false,
      // When the network comes back, refetch silently so cached state
      // catches up. Without this a phone returning from sleep showed
      // stale data until the first user-driven refetch.
      refetchOnReconnect: 'always',
      // Default cache lifetime — chat detail + messages set their own
      // longer values in api.ts so they survive navigation cleanly.
      gcTime: 60 * 60_000,
    },
  },
})
if (!guestBoot) installLocalReadStorePersistence(qc)

// React reached the entrypoint, so any HTML-level stale-chunk recovery from a
// previous failed boot can be armed again for the next real deploy.
try {
  window.sessionStorage.removeItem('agentchat-boot-recovery-at')
  const current = new URL(window.location.href)
  if (current.searchParams.has('agentchat_update') || current.searchParams.has('agentchat_recover')) {
    current.searchParams.delete('agentchat_update')
    current.searchParams.delete('agentchat_recover')
    window.history.replaceState(null, '', `${current.pathname}${current.search}${current.hash}`)
  }
} catch {
  // Storage can be unavailable in hardened/private contexts; boot still works.
}

// Logical app version, bumped in lockstep with sw.js CACHE_NAME. Query cache
// invalidation is intentionally schema-based below so normal updates do not
// cold-start the chat list after Mark taps Reload.
export const APP_VERSION = 'agentchat-v575'
const QUERY_CACHE_SCHEMA_VERSION = 'agentchat-query-cache-v1'

const persister = createSyncStoragePersister({
  storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  key: 'agentchat-query-cache',
  // Prune keys older than 7 days so it doesn't balloon.
  serialize: (data) => JSON.stringify(data),
  deserialize: (s) => JSON.parse(s),
})

function renderApp(): void {
  if (guestBoot) {
    ReactDOM.createRoot(document.getElementById('root')!).render(
      <React.StrictMode><ErrorBoundary><QueryClientProvider client={qc}><BrowserRouter><App /></BrowserRouter></QueryClientProvider></ErrorBoundary></React.StrictMode>,
    )
    return
  }
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <ErrorBoundary>
        <PersistQueryClientProvider
          client={qc}
          persistOptions={{
            persister,
            maxAge: 7 * 24 * 60 * 60_000,   // 7 days
            buster: QUERY_CACHE_SCHEMA_VERSION,
            // Keep localStorage tiny and synchronous. The full offline read
            // model lives in IndexedDB (`localReadStore`), so message history
            // never blocks boot on a giant JSON parse.
            dehydrateOptions: {
              shouldDehydrateQuery: (query) => {
                const key = (query.queryKey?.[0] as string) || ''
                return key === 'chats' || key === 'chat' || key === 'engaged-topics'
              },
            },
          }}
        >
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </PersistQueryClientProvider>
      </ErrorBoundary>
    </React.StrictMode>,
  )
}

async function bootstrap(): Promise<void> {
  await localReadHydration
  renderApp()
}

void bootstrap()
